import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  FacilityTransferDirection,
  type FacilityTransferItemPublicRecord,
  type FacilityTransferRoundPublicRecord
} from '@navio/nsdk';
import { NoddiButton } from 'noddi-ui-web';
import { formatLicensePlate, getCarDisplayName } from 'noddi-util';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { getExternalCheckStatusLabel } from './externalCheckStatus';
import { useFacilityTimeZone } from './facilityTimeZone';
import { groupItemsByRound, type PickupRoundGroup } from './pickupRounds';

interface Props {
  facilityName: string;
  pickupDay: string;
  items: FacilityTransferItemPublicRecord[];
  rounds: FacilityTransferRoundPublicRecord[];
}

export const PickListPrint = ({ facilityName, pickupDay, items, rounds }: Props) => {
  const timeZone = useFacilityTimeZone();
  const contentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef, documentTitle: t`Pick list ${facilityName} ${pickupDay}` });

  const groups: PickupRoundGroup[] =
    rounds.length === 0
      ? [{ key: 'all', marker: '', title: t`Wheel sets`, timeLabel: null, items }]
      : groupItemsByRound(items, rounds, FacilityTransferDirection.OUTGOING, timeZone);

  return (
    <>
      <NoddiButton variant='outline' startIcon='Printer' onClick={handlePrint}>
        <Trans>Print pick list</Trans>
      </NoddiButton>

      <div className='hidden'>
        <div ref={contentRef} className='p-6 text-black'>
          <h1 className='text-2xl font-bold'>
            <Trans>
              Pick list · {facilityName} · {pickupDay}
            </Trans>
          </h1>

          {groups.map((group, index) => (
            <section key={group.key} className={index > 0 ? 'mt-6 break-before-page' : 'mt-6'}>
              <h2 className='text-lg font-bold'>
                {group.title}
                {group.timeLabel && ` · ${group.timeLabel}`} ({group.items.length})
              </h2>
              <table className='mt-2 w-full border-collapse text-sm'>
                <thead>
                  <tr className='border-b-2 border-black text-left'>
                    <th className='w-16 py-1 pr-4'>
                      <Trans>Picked</Trans>
                    </th>
                    <th className='py-1 pr-4'>
                      <Trans>License plate</Trans>
                    </th>
                    <th className='py-1 pr-4'>
                      <Trans>Car</Trans>
                    </th>
                    <th className='py-1 pr-4'>
                      <Trans>Owner</Trans>
                    </th>
                    <th className='py-1 pr-4'>
                      <Trans>Order</Trans>
                    </th>
                    <th className='py-1 pr-4'>
                      <Trans>Status</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item) => (
                    <tr key={item.id} className='break-inside-avoid border-b border-gray-400'>
                      <td className='py-2 pr-4' aria-label={t`Picked`}>
                        <span className='inline-block size-4 border border-black' />
                      </td>
                      <td className='py-2 pr-4 font-semibold'>
                        {item.licensePlate ? formatLicensePlate(item.licensePlate) : item.storageObjectGroupIdentifier}
                      </td>
                      <td className='py-2 pr-4'>
                        {getCarDisplayName({
                          car: { make: item.carMake, model: item.carModel, licensePlate: item.licensePlate },
                          showLicensePlate: false,
                          showYear: false
                        })}
                      </td>
                      <td className='py-2 pr-4'>{item.ownerName}</td>
                      <td className='py-2 pr-4 uppercase'>{item.orderSlug}</td>
                      <td className='py-2 pr-4'>
                        {getExternalCheckStatusLabel(item.externalCheckStatus, FacilityTransferDirection.OUTGOING)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      </div>
    </>
  );
};
